import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { blue, grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { List, Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from "@material-ui/icons/Group";
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import FinalizacaoVendaModal from "../FinalizacaoVendaModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, SwapHoriz, Add } from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility"; // Ícone de spy
import { 
  CheckCircle as CheckCircleIcon, 
  XCircle as XCircleIcon, 
  ArrowUUpRight as ArrowUUpRightIcon
  // Thermometer,
  // Megaphone
} from "@phosphor-icons/react";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import NewTicketModal from "../NewTicketModal";
import {
  Avatar,
  Badge,
  ListItemAvatar,
  ListItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
  Checkbox,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    border: `2px solid ${theme.mode === "light" ? '#BECAE8' : '#333333'}`,
    minHeight:"120px",
    padding: '1px',
    backgroundColor: theme.mode === "light" ? "#FBFCFF" : theme.palette.background.paper,
    borderRadius: "16px",
    marginBottom: theme.spacing(1), // Adiciona espaçamento entre os cards
    display: "flex",
    marginTop:"8px",
    flexDirection: "column",
    fontFamily: '"Poppins", sans-serif',
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "#e1ebff" : "#0b0b0d",
      "& $ticketTagsRow": {
        backgroundColor: theme.mode === "light" ? "#d5e0f7" : "#0b0b0d",
      },
    },
    "&.Mui-selected": {
      backgroundColor: theme.mode === "light" ? "#e1ebff" : "#0c1210",
      "& $ticketTagsRow": {
        backgroundColor: theme.mode === "light" ? "#d5e0f7" : "#0c1210",
      },
      "&:hover": {
        backgroundColor: theme.mode === "light" ? "#e1ebff" : "#0c1210",
        "& $ticketTagsRow": {
          backgroundColor: theme.mode === "light" ? "#d5e0f7" : "#0c1210",
        },
      },
    },
  },

  pendingTicket: {
    cursor: "unset",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  ticketInfoWrapper: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    gap: 16,
    flex: 1,
    width: "100%",
    boxSizing: "border-box",
  },
  ticketAvatarContainer: {
    position: "relative",
    width: 73,
    height: 73,
    flexShrink: 0,
  },
  ticketAvatar: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
  },
  connectionBadge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    backgroundColor: "#25d366",
    color: "white",
    display: "inline",
    padding: "2px",
    borderRadius: "100%",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    "& svg": { // Força a cor diretamente na tag SVG caso o componente filho tenha uma cor fixa
      fill: "#ffffff",
      color: "#ffffff"
    }
  },
  ticketContent: {
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "stretch",
    overflow: "hidden",
  },
  ticketDetails: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    overflow: "hidden",
  },
  ticketNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  unreadBadge: {
    backgroundColor: "#2196F3",
    color: "white",
    borderRadius: "50%",
    minWidth: 18,
    height: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: "'Poppins', sans-serif",
  },
  ticketName: {
    color: theme.mode === "light" ? "#0E1433" : "#FFF",
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  ticketMessage: {
    color: theme.mode === "light" ? "#717E9F" : "grey[400]",
    fontSize: 12,
    fontWeight: 400,
    fontFamily: "'Poppins', sans-serif",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: 4,
  },
  ticketBadges: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
  },
  ticketTagsRow: {
    display: "flex",
    minHeight: "30px",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    padding: "4px 16px",
    width: "100%",  
    boxSizing: "border-box",
    backgroundColor: theme.mode === "light" ? "#EEF3FF" : "#0a0a0c",
    borderBottomLeftRadius: "13px",
    borderBottomRightRadius: "13px",
    transition: "background-color 0.2s ease-in-out",
    "& [class*='makeStyles-tag']": {
      fontSize: "0.7em !important",
      textTransform: "none !important",
    },
  },
  tagsLabel: {
    fontSize: "12px",
    fontWeight: 400,
    color: theme.mode === "light" ? "#181818" : "#ededed",
    marginRight: 4,
  },
  badgeFilled: {
    padding: "2px 6px",
    borderRadius: 4,
    color: "white",
    fontSize: 10,
    fontWeight: 400,
    fontFamily: "'Poppins', sans-serif",
  },
  badgeOutlined: {
    padding: "2px 6px",
    borderRadius: 4,
    color: theme.mode === "light" ? "none" : "#FFF",
    border: `1px solid ${theme.mode === "light" ? "#97A2BE" : "#666"}`,
    fontSize: 10,
    fontWeight: 400,
    fontFamily: "'Poppins', sans-serif",
  },
  placeholderBadge: {
    padding: "2px 6px",
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 400,
    fontFamily: "'Poppins', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    textTransform: 'uppercase',
  },
  scoreBadgeHot: { backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' },
  scoreBadgeWarm: { backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fde68a' },
  scoreBadgeCold: { backgroundColor: '#e0f2fe', color: '#2563eb', border: '1px solid #bae6fd' },
  adsBadge: { backgroundColor: '#f3e8ff', color: '#4f46e5', border: '1px solid #e9d5ff' },
  ticketRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "space-between",
    alignSelf: "stretch",
    flexShrink: 0,
    marginLeft: 12,
  },
  ticketTimeRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  ticketTime: {
    color: theme.mode === "light" ? "#2D3456" : grey[400],
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
  },
  ticketActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  actionButton: {
    backgroundColor: "transparent",
    boxShadow: "none",
    border: "none",
    padding: 0,
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "transparent",
      opacity: 0.8
    },
  },
  customCheckbox: {
    padding: 0,
    accentColor: '#0046F9',
    "& .MuiSvgIcon-root": {
      width: "18px",
      height: "18px",
      borderRadius: "2px",
      color: theme.mode === "light" ? "#0046F9" : theme.palette.primary.main,
    }
  },

  // Estilos para o modal da imagem
  imageModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imageModalContent: {
    outline: "none",
    maxWidth: "90vw",
    maxHeight: "90vh",
  },
  expandedImage: {
    width: "100%",
    height: "auto",
    maxWidth: "500px",
    borderRadius: theme.spacing(1),
  },
  clickableAvatar: {
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
  }
}));

const TicketListItemCustom = ({ setTabOpen, ticket, selectedTickets, setSelectedTickets }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [
    acceptTicketWithouSelectQueueOpen,
    setAcceptTicketWithouSelectQueueOpen,
  ] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  // Estados para o modal de finalização de venda
  const [openFinalizacaoVenda, setOpenFinalizacaoVenda] = useState(false);
  const [finalizacaoTipo, setFinalizacaoTipo] = useState(null);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);
  const [showFinalizacaoOptions, setShowFinalizacaoOptions] = useState(false);

  const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem

  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Função para fechar modal da imagem
  const handleImageModalClose = () => {
    setImageModalOpen(false);
  };

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
    setAcceptTicketWithouSelectQueueOpen(true);
  }, []);

  const handleCloseTransferTicketModal = useCallback(() => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  }, []);

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(false);
    setLoading(false);
  }, []);

  if (!ticket || !ticket.id) return null;

  const handleCloseTicket = async (id) => {
    if (!id) return;
    // Verificar se a finalização com valor de venda está ativa
    if (
      user?.finalizacaoComValorVendaAtiva === true ||
      user?.finalizacaoComValorVendaAtiva === "true"
    ) {
      // Se estiver ativa, abrir o modal de finalização de venda
      setFinalizacaoTipo("comDespedida");
      setOpenFinalizacaoVenda(true);
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket?.uuid}`);
    } else {
      // Comportamento original
      let setting;
      try {
        setting = await getSetting({ column: "requiredTag" });
      } catch (err) {
        toastError(err);
        return; // Aborta caso não consiga buscar a configuração
      }

      if (setting?.requiredTag === "enabled") {
        //verificar se tem uma tag
        try {
          const contactTags = await api.get(
            `/contactTags/${ticket?.contact?.id}`
          );
          if (!contactTags.data.tags) {
            toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
          } else {
            setLoading(true);
            await api.put(`/tickets/${id}`, {
              status: "closed",
              userId: user?.id || null,
            });

            if (isMounted.current) {
              setLoading(false);
            }

            history.push(`/tickets/`);
          }
        } catch (err) {
          if (isMounted.current) {
            setLoading(false);
          }
          toastError(err);
        }
      } else {
        setLoading(true);
        try {
          await api.put(`/tickets/${id}`, {
            status: "closed",
            userId: user?.id || null,
          });

          if (isMounted.current) {
            setLoading(false);
          }
          history.push(`/tickets/`);
        } catch (err) {
          if (isMounted.current) {
            setLoading(false);
          }
          toastError(err);
        }
      }
    }
  };

  const handleCloseIgnoreTicket = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }

    history.push(`/tickets/`);
  };

  const truncate = (str, len) => {
    if (!isNil(str)) {
      if (str.length > len) {
        return str.substring(0, len) + "...";
      }
      return str;
    }
  };

  const handleOpenTransferModal = () => {
    setLoading(true);
    setTransferTicketModalOpen(true);
    if (isMounted.current) {
      setLoading(false);
    }
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket?.uuid}`);
  };

  const handleOpenNewTicketModal = () => {
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = (newTicket) => {
    setNewTicketModalOpen(false);
    if (newTicket && newTicket.uuid) {
      // Se um novo ticket foi criado, redirecionar para ele
      handleSelectTicket(newTicket);
      history.push(`/tickets/${newTicket.uuid}`);
    }
  };

  const handleAcepptTicket = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const otherTicket = await api.put(`/tickets/${id}`, {
        status:
          ticket?.isGroup && ticket?.channel === "whatsapp" ? "group" : "open",
        userId: user?.id,
      });

      if (otherTicket.data.id !== ticket?.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTabOpen(ticket?.isGroup ? "group" : "open");
          handleSelectTicket(otherTicket.data);
          history.push(`/tickets/${otherTicket.uuid}`);
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: "sendGreetingAccepted",
          });
        } catch (err) {
          toastError(err);
        }

        if (
          setting.sendGreetingAccepted === "enabled" &&
          (!ticket?.isGroup || ticket?.whatsapp?.groupAsTicket === "enabled")
        ) {
          handleSendMessage(ticket?.id);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        setTabOpen(ticket?.isGroup ? "group" : "open");
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket?.uuid}`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id) => {
    if (!id) return;
    let setting;

    try {
      setting = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      toastError(err);
    }
    if (!setting.greetingAcceptedMessage) {
      toast.warning(
        i18n.t("messagesList.header.buttons.greetingAcceptedMessage")
      );
      return;
    }
    const msg = `${setting.greetingAcceptedMessage}`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket || {};
    if (!id) return;
    setCurrentTicket({ id, uuid, code });
  };

  const handleUpdateTicketStatusWithData = async (
    ticketData,
    sendFarewellMessage,
    finalizacaoMessage
  ) => {
    try {
      await api.put(`/tickets/${ticket?.id}`, {
        ...ticketData,
        sendFarewellMessage,
        finalizacaoMessage,
      });
      toast.success("Ticket finalizado com sucesso!");
      history.push(`/tickets/`);
    } catch (err) {
      toastError(err);
    }
  };

  // Função para espionar ticket chatbot
  const handleSpyTicket = () => {
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket?.uuid}`);
  };

  // Lógica de permissão para mensagens pending - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const shouldBlurMessages = ticket?.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

  // Função para renderizar a mensagem com base na permissão - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const renderLastMessage = () => {
    if (shouldBlurMessages) {
      return (
        <MarkdownWrapper>
          {i18n.t("tickets.messageHidden") || "Mensagem oculta"}
        </MarkdownWrapper>
      );
    }

    if (!ticket?.lastMessage) {
      return <br />;
    }

    if (ticket?.lastMessage?.includes("data:image/png;base64")) {
      return <MarkdownWrapper>Localização</MarkdownWrapper>;
    }

    if (ticket?.lastMessage?.includes("BEGIN:VCARD")) {
      return <MarkdownWrapper>Contato</MarkdownWrapper>;
    }

    return (
      <MarkdownWrapper>
        {truncate(ticket?.lastMessage, 40)}
      </MarkdownWrapper>
    );
  };

  const isSelected = selectedTickets?.includes(ticket?.id);
  const handleToggleSelect = (e) => {
    e.stopPropagation();
    if (isSelected) {
      setSelectedTickets(selectedTickets.filter(id => id !== ticket?.id));
    } else {
      setSelectedTickets([...selectedTickets, ticket?.id]);
    }
  };

  // const scoringInfo = ticket?.scoringInfo || { leadScore: 0, temperatureStatus: 'cold', isFromAds: false, actionAlerts: {} };

  return (
    <React.Fragment key={ticket?.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
          ticketId={ticket?.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket?.id}
          ticket={ticket}
        />
      )}
      {newTicketModalOpen && (
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseNewTicketModal}
          initialContact={ticket?.contact}
        />
      )}
      <ListItem
        button
        dense
        disableGutters
        onClick={(e) => {
          const tagName = e.target?.tagName?.toLowerCase();
          const type = e.target?.type;
          const isCheckboxClicked =
            (tagName === "input" && type === "checkbox") ||
            (tagName === "svg" && type === undefined) ||
            (tagName === "path" && type === undefined);

          if (isCheckboxClicked) return;

          handleSelectTicket(ticket);
        }}
        selected={ticketId && ticketId === ticket?.uuid}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket?.status === "pending",
        })}
      >
        <div className={classes.ticketInfoWrapper}>
        <div className={classes.ticketAvatarContainer}>
          <Avatar
            src={`${ticket?.contact?.urlPicture}`}
            className={clsx(classes.ticketAvatar, classes.clickableAvatar)}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectTicket(ticket);
            }}
          />
          {ticket?.channel && (
            <div className={classes.connectionBadge}>
              <ConnectionIcon width="16" height="16" connectionType={ticket?.channel} />
            </div>
          )}
        </div>

        <div className={classes.ticketContent}>
          <div className={classes.ticketDetails}>
            <div className={classes.ticketNameRow}>
              {ticket?.isGroup && ticket?.channel === "whatsapp" && (
                <GroupIcon fontSize="small" style={{ color: grey[700] }} />
              )}
              <Typography className={classes.ticketName}>
                {ticket?.contact?.name}
              </Typography>
              {Number(ticket?.unreadMessages) > 0 && !shouldBlurMessages && (
                <div className={classes.unreadBadge}>{ticket?.unreadMessages}</div>
              )}
              {shouldBlurMessages && (
                <div className={classes.unreadBadge}>?</div>
              )}
            </div>

            <div className={classes.ticketMessage}>
              {renderLastMessage()}
            </div>

            <div className={classes.ticketBadges}>
              <div className={classes.badgeFilled} style={{ backgroundColor: ticket?.queue?.color || "#7c7c7c" }}>
                {ticket?.queueId ? ticket?.queue?.name : ticket?.status === "lgpd" ? "LGPD" : i18n.t("momentsUser.noqueue")}
              </div>
              {ticket?.user && (
                <div className={classes.badgeOutlined}>
                  {ticket?.user?.name}
                </div>
              )}
              {ticket?.whatsapp && (
                <div className={classes.badgeOutlined}>
                  {ticket.whatsapp?.name}
                </div>
              )}
              {/* LEAD SCORING: Badges de Temperatura e Origem desativadas visualmente
              <Tooltip title={`Temperatura: ${scoringInfo.leadScore}/100`}>
                <div className={clsx(classes.placeholderBadge, {
                  [classes.scoreBadgeHot]: scoringInfo.temperatureStatus === 'hot',
                  [classes.scoreBadgeWarm]: scoringInfo.temperatureStatus === 'warm',
                  [classes.scoreBadgeCold]: scoringInfo.temperatureStatus === 'cold',
                })}>
                  <Thermometer size={12} weight="bold" /> {scoringInfo.leadScore}
                </div>
              </Tooltip>
              {scoringInfo.isFromAds && (
                <Tooltip title="Origem: Tráfego Pago (Ads)">
                  <div className={clsx(classes.placeholderBadge, classes.adsBadge)}>
                    <Megaphone size={12} weight="bold" /> Ads
                  </div>
                </Tooltip>
              )}
              */}
            </div>
          </div>

          <div className={classes.ticketRight}>
            <div className={classes.ticketTimeRow}>
              {ticket?.lastMessage && (
                <Typography className={classes.ticketTime}>
                  {isSameDay(parseISO(ticket?.updatedAt || new Date().toISOString()), new Date()) ? format(parseISO(ticket?.updatedAt || new Date().toISOString()), "HH:mm") : format(parseISO(ticket?.updatedAt || new Date().toISOString()), "dd/MM/yyyy")}
                </Typography>
              )}
              <Checkbox
                checked={isSelected || false}
                onChange={handleToggleSelect}
                color="primary"
                className={classes.customCheckbox}
              />
            </div>

            <div className={classes.ticketActions}>
              {/* Para tickets com status chatbot, mostrar apenas o ícone de spy */}
              {ticket?.status === "chatbot" && (
              <ButtonWithSpinner
                  className={classes.actionButton}
                style={{
                    color: theme.mode === "light" ? "blue" : "#FFF",
                }}
                variant="contained"
                size="small"
                loading={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSpyTicket();
                }}
              >
                <Tooltip title="Espiar conversa do chatbot">
                    <VisibilityIcon fontSize="small" />
                </Tooltip>
              </ButtonWithSpinner>
              )}

              {/* Para todos os outros status, manter os botões originais */}
              {ticket?.status !== "chatbot" && (
                <>
                  {ticket?.status === "pending" && (ticket?.queueId === null || ticket?.queueId === undefined) && (
                    <ButtonWithSpinner
                      className={classes.actionButton}
                      variant="text"
                      size="small"
                      loading={loading}
                      onClick={(e) => handleOpenAcceptTicketWithouSelectQueue()}
                    >
                      <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                        <CheckCircleIcon size={22} weight="fill" color="#02B881" />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}

                  {ticket?.status === "pending" && ticket?.queueId !== null && (
                  <ButtonWithSpinner
                      className={classes.actionButton}
                    variant="text"
                    size="small"
                    loading={loading}
                  onClick={(e) => handleAcepptTicket(ticket?.id)}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                        <CheckCircleIcon size={22} weight="fill" color="#02B881" />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}

                  {(ticket?.status === "pending" || ticket?.status === "open" || ticket?.status === "group") && (
                  <ButtonWithSpinner
                      className={classes.actionButton}
                    variant="text"
                    size="small"
                    loading={loading}
                    onClick={handleOpenTransferModal}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.transfer")}`}>
                        <ArrowUUpRightIcon size={22} weight="fill" color={theme.mode === "light" ? "#0E1433" : "#FFF"} />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}

                  {(ticket?.status === "open" || ticket?.status === "group") && (
                  <ButtonWithSpinner
                      className={classes.actionButton}
                    variant="text"
                    size="small"
                    loading={loading}
                  onClick={(e) => handleCloseTicket(ticket?.id)}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.closed")}`}>
                        <XCircleIcon size={22} weight="fill" color="#F03737" />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}

                  {(ticket?.status === "pending" || ticket?.status === "lgpd") && (user?.userClosePendingTicket === "enabled" || user?.profile === "admin") && (
                    <ButtonWithSpinner
                        className={classes.actionButton}
                      variant="text"
                      size="small"
                      loading={loading}
                    onClick={(e) => handleCloseIgnoreTicket(ticket?.id)}
                    >
                      <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                          <XCircleIcon size={22} weight="fill" color="#F03737" />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}

                  {ticket?.status === "closed" && (
                  <ButtonWithSpinner
                      className={classes.actionButton}
                    style={{
                        color: theme.mode === "light" ? theme.palette.primary.main : "#FFF",
                    }}
                    variant="contained"
                    size="small"
                    loading={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenNewTicketModal();
                    }}
                  >
                    <Tooltip title="Criar Novo Ticket">
                        <Add fontSize="small" />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
                </>
              )}
            </div>
          </div>
        </div>
        </div>
        <div className={classes.ticketTagsRow}>
          <span className={classes.tagsLabel}>Tags:</span>
          {ticket?.contact?.tags?.map((tag, index) => (
            <ContactTag tag={tag} key={`ticket-contact-tag-${ticket?.id}-${tag?.id || index}`} />
          ))}
          {ticket?.tags?.map((tag, index) => (
            <ContactTag tag={tag} key={`ticket-ticket-tag-${ticket?.id}-${tag?.id || index}`} />
          ))}
        </div>
      </ListItem>

      {/* Modal de Finalização de Venda */}
      {openFinalizacaoVenda && (
        <FinalizacaoVendaModal
          open={openFinalizacaoVenda}
          onClose={() => setOpenFinalizacaoVenda(false)}
          ticket={ticket}
          onFinalizar={(ticketData) => {
            setOpenFinalizacaoVenda(false);
            setTicketDataToFinalize(ticketData);
            setShowFinalizacaoOptions(true);
          }}
        />
      )}

      {/* Modal de Opções de Finalização */}
      {showFinalizacaoOptions && (
        <Dialog
          open={showFinalizacaoOptions}
          onClose={() => setShowFinalizacaoOptions(false)}
          aria-labelledby="finalizacao-options-title"
        >
          <DialogTitle id="finalizacao-options-title">
            Como deseja finalizar?
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  false,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingWithoutFarewellMsg")}
            </Button>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  true,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingCancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal da Imagem */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImageModalClose}
        className={classes.imageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogContent className={classes.imageModalContent}>
          <img 
            src={ticket?.contact?.urlPicture} 
            alt={ticket?.contact?.name || "Foto do contato"}
            className={classes.expandedImage}
          />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default TicketListItemCustom;